import { Injectable } from '@angular/core';

export interface LocationCoordinates {
  lat: number;
  lng: number;
}

export interface InteractiveLocationMapOptions {
  defaultCenter: LocationCoordinates;
  defaultZoom: number;
  selectedZoom: number;
  initialCoords?: LocationCoordinates | null;
  onLocationChange: (lat: number, lng: number) => void;
}

export interface PreviewLocationMapOptions {
  defaultCenter: LocationCoordinates;
  defaultZoom: number;
  selectedZoom: number;
  coords?: LocationCoordinates | null;
}

export class InteractiveLocationMapHandle {
  readonly map: google.maps.Map;
  private marker: google.maps.Marker | null = null;
  private autocomplete: google.maps.places.Autocomplete | null = null;
  private readonly listeners: google.maps.MapsEventListener[] = [];
  private readonly selectedZoom: number;
  private readonly onLocationChange: (lat: number, lng: number) => void;

  constructor(
    map: google.maps.Map,
    options: Pick<InteractiveLocationMapOptions, 'selectedZoom' | 'onLocationChange'>,
  ) {
    this.map = map;
    this.selectedZoom = options.selectedZoom;
    this.onLocationChange = options.onLocationChange;

    this.listeners.push(
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        const latLng = event.latLng;
        if (!latLng) {
          return;
        }
        this.setMarker(latLng.lat(), latLng.lng(), true);
        this.onLocationChange(latLng.lat(), latLng.lng());
      }),
    );
  }

  bindSearchInput(searchInput: HTMLInputElement): void {
    this.autocomplete = new google.maps.places.Autocomplete(searchInput, {
      fields: ['geometry', 'formatted_address', 'name'],
      componentRestrictions: { country: 'in' },
    });
    this.autocomplete.bindTo('bounds', this.map);

    this.listeners.push(
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete?.getPlace();
        const location = place?.geometry?.location;
        if (!location) {
          return;
        }
        const lat = location.lat();
        const lng = location.lng();
        this.setMarker(lat, lng, true);
        this.onLocationChange(lat, lng);
      }),
    );
  }

  setMarker(lat: number, lng: number, animate = true): void {
    const position = { lat, lng };

    if (this.marker) {
      this.marker.setPosition(position);
    } else {
      this.marker = new google.maps.Marker({
        map: this.map,
        position,
        draggable: true,
        animation: animate ? google.maps.Animation.DROP : undefined,
      });

      this.listeners.push(
        this.marker.addListener('dragend', () => {
          const draggedPosition = this.marker?.getPosition();
          if (!draggedPosition) {
            return;
          }
          this.onLocationChange(draggedPosition.lat(), draggedPosition.lng());
        }),
      );
    }

    this.map.setCenter(position);
    this.map.setZoom(this.selectedZoom);
  }

  destroy(): void {
    this.listeners.forEach((listener) => listener.remove());
    this.marker?.setMap(null);
    this.marker = null;
    this.autocomplete = null;
  }
}

export class PreviewLocationMapHandle {
  readonly map: google.maps.Map;
  private marker: google.maps.Marker | null = null;

  constructor(container: HTMLElement, options: PreviewLocationMapOptions) {
    const center = options.coords ?? options.defaultCenter;

    this.map = new google.maps.Map(container, {
      center,
      zoom: options.coords ? options.selectedZoom : options.defaultZoom,
      gestureHandling: 'none',
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });

    if (options.coords) {
      this.marker = new google.maps.Marker({
        map: this.map,
        position: options.coords,
        draggable: false,
      });
    }
  }

  destroy(): void {
    this.marker?.setMap(null);
    this.marker = null;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleMapsLocationService {
  private loadPromise: Promise<void> | null = null;

  load(apiKey: string): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    if (typeof google !== 'undefined' && google.maps?.places) {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const callbackName = '__flyashGoogleMapsInit';
      const existingScript = document.querySelector(
        'script[data-flyash-google-maps="true"]',
      ) as HTMLScriptElement | null;

      if (existingScript) {
        if (typeof google !== 'undefined' && google.maps?.places) {
          resolve();
          return;
        }

        existingScript.addEventListener('load', () => resolve());
        existingScript.addEventListener('error', () =>
          reject(new Error('Failed to load Google Maps')),
        );
        return;
      }

      const callbackWindow = window as unknown as Window & Record<string, () => void>;
      callbackWindow[callbackName] = () => {
        delete callbackWindow[callbackName];
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.dataset['flyashGoogleMaps'] = 'true';
      script.onerror = () => reject(new Error('Failed to load Google Maps'));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  createInteractiveMap(
    container: HTMLElement,
    options: InteractiveLocationMapOptions,
  ): InteractiveLocationMapHandle {
    const center = options.initialCoords ?? options.defaultCenter;
    const map = new google.maps.Map(container, {
      center,
      zoom: options.initialCoords ? options.selectedZoom : options.defaultZoom,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    const handle = new InteractiveLocationMapHandle(map, {
      selectedZoom: options.selectedZoom,
      onLocationChange: options.onLocationChange,
    });

    if (options.initialCoords) {
      handle.setMarker(options.initialCoords.lat, options.initialCoords.lng, false);
    }

    return handle;
  }

  createPreviewMap(
    container: HTMLElement,
    options: PreviewLocationMapOptions,
  ): PreviewLocationMapHandle {
    return new PreviewLocationMapHandle(container, options);
  }

  parseCoordinates(latitude: unknown, longitude: unknown): LocationCoordinates | null {
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  }
}
